import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  MARGIN_INTEL_MAX_IDS,
  type MarginIntel,
  type MarginIntelRowsResponse,
} from '@siliconx/shared';
import { api, throwIfNotOk } from './api';
import { qk } from './queryKeys';
import { useOrgFeature } from './orgFeatures';

/** Rows beyond this many per list show a dash (v1: bounded request count). */
export const MARGIN_INTEL_MAX_ROWS = MARGIN_INTEL_MAX_IDS * 5;

export interface MarginIntelRows {
  assets: Record<string, MarginIntel>;
  listings: Record<string, MarginIntel>;
  costsConfigured: boolean;
}

function chunk<T>(xs: T[], n: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
  return out;
}

/**
 * SIL-2543: inline margin figures for the rows on screen. One query keyed by
 * the sorted id sets (a filter change that leaves the same rows does not
 * refire); disabled unless the org holds `marketIntel` and rows exist. Any
 * failure leaves `data` undefined, i.e. cells render a dash and the row grid
 * is never blocked.
 */
export function useMarginIntel(
  assetIds: readonly string[],
  listingIds: readonly string[],
  targetMarginPct?: number,
) {
  const enabledFeature = useOrgFeature('marketIntel');
  const assets = useMemo(() => [...new Set(assetIds)].sort().slice(0, MARGIN_INTEL_MAX_ROWS), [assetIds]);
  const listings = useMemo(() => [...new Set(listingIds)].sort().slice(0, MARGIN_INTEL_MAX_ROWS), [listingIds]);
  const key = JSON.stringify([assets, listings, targetMarginPct ?? null]);

  return useQuery<MarginIntelRows>({
    queryKey: qk.marketIntel.inlineMargins(key),
    enabled: enabledFeature && assets.length + listings.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const merged: MarginIntelRows = { assets: {}, listings: {}, costsConfigured: true };
      const jobs = [
        ...chunk(assets, MARGIN_INTEL_MAX_IDS).map((ids) => ({ assetIds: ids, listingIds: [] as string[] })),
        ...chunk(listings, MARGIN_INTEL_MAX_IDS).map((ids) => ({ assetIds: [] as string[], listingIds: ids })),
      ];
      for (const job of jobs) {
        const res = await api.api.client['margin-intel'].rows.$post({
          json: { ...job, ...(targetMarginPct !== undefined ? { targetMarginPct } : {}) },
        });
        if (!res.ok) {
          await throwIfNotOk(res);
          throw new Error(`HTTP ${res.status}`);
        }
        const body = (await res.json()) as unknown as MarginIntelRowsResponse;
        Object.assign(merged.assets, body.assets);
        Object.assign(merged.listings, body.listings);
        merged.costsConfigured = merged.costsConfigured && body.costsConfigured;
      }
      return merged;
    },
  });
}
